import { Button, Input, Modal, Select, Space, Typography, notification } from '@pankod/refine-antd';
import { useInput } from 'hooks/useInput';
import { useEffect, useState } from 'react';
import { ServiceCategory, createAppInstance, getServiceCategories } from 'services/app-instance';
import useAppInstanceStore from 'store/useAppInstanceStore';

interface AppInstanceCreateModalProps {
  isModalOpen: boolean;
  closeModal: () => void;
}

const { Text } = Typography;
const DEFAULT_SERVICE_CATEGORY: ServiceCategory = {
  id: 'default',
  name: 'Default',
  displayName: 'Default',
};

const withDefaultCategory = (items: ServiceCategory[]) => {
  if (items.some((category) => category.id === DEFAULT_SERVICE_CATEGORY.id)) {
    return items;
  }
  return [DEFAULT_SERVICE_CATEGORY, ...items];
};

export const AppInstanceCreateModal: React.FC<AppInstanceCreateModalProps> = ({
  isModalOpen,
  closeModal,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCategoryLoading, setIsCategoryLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<ServiceCategory[]>([DEFAULT_SERVICE_CATEGORY]);
  const [categoryId, setCategoryId] = useState<string>();
  const [seedDataMode, setSeedDataMode] = useState<'skip' | 'load'>('skip');
  const nameInput = useInput('');
  const descriptionInput = useInput('');
  const { addAppInstance } = useAppInstanceStore();

  useEffect(() => {
    if (!isModalOpen) {
      setCategoryId(undefined);
      setSeedDataMode('skip');
      return;
    }

    let isMounted = true;
    const fetchCategories = async () => {
      setIsCategoryLoading(true);
      try {
        const fetchedCategories = await getServiceCategories();
        if (isMounted) {
          setCategories(withDefaultCategory(fetchedCategories || []));
        }
      } catch (err: any) {
        console.error(err);
        notification.error({
          message: 'Failed',
          description: err?.message || 'Failed to load categories.',
        });
      } finally {
        if (isMounted) {
          setIsCategoryLoading(false);
        }
      }
    };

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, [isModalOpen]);

  const handleSubmit = async () => {
    if (!nameInput.value.trim() || !descriptionInput.value.trim() || !categoryId) {
      notification.warning({
        message: 'Required',
        description: 'Please provide name, description, and select a category.',
      });
      return;
    }

    setIsLoading(true);
    try {
      const data = {
        name: nameInput.value,
        description: descriptionInput.value,
        categoryId,
        seedData: seedDataMode === 'load' ? true : undefined,
      };
      const response = await createAppInstance(
        data.name,
        data.description,
        data.categoryId,
        data.seedData
      );
      const { message, ...appInstance } = response;
      addAppInstance(appInstance);
      if (message) {
        notification.info({
          message: 'Success',
          description: message,
        });
      }
    } catch (err: any) {
      console.error(err);
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);

    closeModal();
    nameInput.reset();
    descriptionInput.reset();
    setCategoryId(undefined);
    setSeedDataMode('skip');
  };

  return (
    <Modal
      title="Create Setup"
      visible={isModalOpen}
      onOk={closeModal}
      onCancel={closeModal}
      footer={[
        <Button key="back" onClick={closeModal}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={isLoading} onClick={handleSubmit}>
          Create
        </Button>,
      ]}
    >
      <>
        <Space direction="vertical" style={{ display: 'block', marginBottom: '1rem' }}>
          <Text style={{ display: 'inline-block', marginBottom: '0.5rem', fontWeight: 600 }}>
            Category
          </Text>
          <Select
            placeholder="Select service category"
            value={categoryId}
            loading={isCategoryLoading}
            onChange={setCategoryId}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="children"
          >
            {categories.map((category) => (
              <Select.Option key={category.id} value={category.id}>
                {category.displayName || category.name}
              </Select.Option>
            ))}
          </Select>
        </Space>

        <Space direction="vertical" style={{ display: 'block', marginBottom: '1rem' }}>
          <Text style={{ display: 'inline-block', marginBottom: '0.5rem', fontWeight: 600 }}>
            Data Setup
          </Text>
          <Select
            value={seedDataMode}
            onChange={(value: 'skip' | 'load') => setSeedDataMode(value)}
            style={{ width: '100%' }}
          >
            <Select.Option value="skip">Skip demo data</Select.Option>
            <Select.Option value="load">Load demo Data</Select.Option>
          </Select>
        </Space>

        <Space direction="vertical" style={{ display: 'block', marginBottom: '1rem' }}>
          <Text style={{ display: 'inline-block', marginBottom: '0.5rem', fontWeight: 600 }}>
            Name
          </Text>
          <Input
            placeholder="Identify this new setup as..."
            value={nameInput.value}
            onChange={nameInput.onChange}
          />
        </Space>

        <Space direction="vertical" style={{ display: 'block' }}>
          <Text style={{ display: 'inline-block', marginBottom: '0.5rem', fontWeight: 600 }}>
            Description
          </Text>
          <Input
            placeholder="Describe the use of this setup..."
            value={descriptionInput.value}
            onChange={descriptionInput.onChange}
          />
        </Space>
      </>
    </Modal>
  );
};
